import { makeAutoObservable } from "mobx";
import { CARRIER_NOTIFICATION, CP_DIMENSION_UNIT, CUSTOM_PACKAGE_SUPPORT_TYPE, DANGEROUS_GOODS_TYPE, IReturnLabelSettings, IReturnMethodAddress, IReturnLabelSettings as IShippingLabelSettings, LABEL_TYPE, ShippingInstructionsChangeTrigger, TRACKING_TYPE, WEIGHT_UNIT } from "../return-method-types";
import { IShippingConnection } from "../../../models/IntegrationsTypes";
import { IOption } from "../../../components/inputs/select-dropdown/select-dropdown";
import { IReturnMethodsSettings } from "../../../services/api/return-methods-service";
import { IChangeable } from "../../../stores/types";
import ReturnAddressStore from "./return-address-store";
import AggregatorsInfoStore from "./aggregators-info-store";
import { SHIPMENT_STATUS_ENUM } from "../../manage-returns/rma-page/rma-types";
import DefaultCarrierSelectorStore from "./default-carrier-selector-store";
import CarrierSelectionOptionsStore from "./carrier-selection-options-store";
import { CarrierOptionsCacheStore } from "./carrier-options-cache-store";
import { getNullableChangesOrModel, isSet } from "../../../models/model-types-methods";
import { IInputValitationResponse } from "../../../hooks/input-validation-hook";

export interface IShippingLabelSettingsChanges extends Omit<Partial<IShippingLabelSettings>, 'service_code'|'package_code'|'carrier_code'>{
    service_code?: string|null;
    package_code?: string|null;
    carrier_code?: string|null;
};

export class ShippingLabelSettingsStore implements IChangeable<IShippingLabelSettings> {

    private _model?: IShippingLabelSettings = {} as IShippingLabelSettings;
    private _changes: IShippingLabelSettingsChanges = {};
    private _aggregatorsInfoStore: AggregatorsInfoStore;
    private _webhookUrl?: string;
    private _addressStore: ReturnAddressStore;
    private _defaultCarrierSelectorStore?: DefaultCarrierSelectorStore;
    private _getModel(): IShippingLabelSettings | undefined { return this._model; }
    private _carrierSelectionOptionsStore?: CarrierSelectionOptionsStore;
    private _onTriggerInstructionsChange: (trigger: ShippingInstructionsChangeTrigger) => void
    private _carrierOptionsCacheStore: CarrierOptionsCacheStore;
    private _scope = 'dashboard-return-method-editor';

    constructor(
        settings:IReturnMethodsSettings,
        addressStore: ReturnAddressStore,
        aggregatorsInfoStore: AggregatorsInfoStore,
        onTriggerInstructionsChange: (trigger: ShippingInstructionsChangeTrigger) => void,
        private _getShippingIntegrations: () => IShippingConnection[],
    )
    {
        this._aggregatorsInfoStore = aggregatorsInfoStore;
        this._webhookUrl = settings.shipping_api_webhook_base_url;
        this._addressStore = addressStore;
        this._onTriggerInstructionsChange = onTriggerInstructionsChange;
        this._carrierOptionsCacheStore = new CarrierOptionsCacheStore();

        makeAutoObservable(this, {}, { autoBind: true });
	}

    public isLoadign(): boolean {
        return this._defaultCarrierSelectorStore?.isLoading() ?? false;
    }

    public get defaultCarrierSelectorStore(): DefaultCarrierSelectorStore | undefined {
        return this._defaultCarrierSelectorStore;
    }

    public get deleted() { return this._changes._deleted ?? false };
    public get id() { return this._model?.id; }
    public get carrierNotifications() { return this._changes.carrier_notifications ?? this._model?.carrier_notifications; }
    public get cpDimensionsUnit() { return this._changes.cp_dimension_unit ?? this._model?.cp_dimension_unit; }
    public get cpHeight() { return getNullableChangesOrModel(this._changes.cp_height, this._model?.cp_height); }
    public get cpLength() { return getNullableChangesOrModel(this._changes.cp_length, this._model?.cp_length); }
    public get cpWidth() { return getNullableChangesOrModel(this._changes.cp_width, this._model?.cp_width); }
    public get labelFormat() { return this._changes.label_format ?? this._model?.label_format; }
    public get labelSize() { return this._changes.label_size ?? this._model?.label_size; }
    public get labelType() { return this._changes.label_type ?? this._model?.label_type; }
    public get returnMethodId() { return this._changes.return_method_id ?? this._model?.return_method_id; }
    public get sendOrderNumber() { return this._changes.send_order_number ?? this._model?.send_order_number; }
    public get shippingConnectionId() { return this._changes.shipping_connection_id ?? this._model?.shipping_connection_id; }
    public get useCustomPackage() { return this._changes.use_custom_package ?? this._model?.use_custom_package; }
    public get useForwardLabels() { return this._changes.use_forward_labels ?? this._model?.use_forward_labels ?? false; }
    public get weight() { return getNullableChangesOrModel(this._changes.weight, this._model?.weight); }
    public get packageWeight() { return getNullableChangesOrModel(this._changes.package_weight, this._model?.package_weight); }
    public get requireSignature() { return this._changes.is_signature_confirmation_required ?? this._model?.is_signature_confirmation_required ?? false; }
    public get ignoreAddressValidation() { return this._changes.ignore_address_validation ?? this._model?.ignore_address_validation; }
    public get boxFree() { return this._changes.is_box_free ?? this._model?.is_box_free ?? this._aggregatorsInfoStore.getBoxFreeInitialValue(this.aggregatorCode); }
    public get defaultHsCode() { return this._changes.default_hs_code ?? this._model?.default_hs_code }
    public get defaultCustomerCompany() { return this._changes.default_customer_company ?? this._model?.default_customer_company }
    public get arrivedAtHubToInternalStatus() { return this._changes.arrived_at_hub_to_internal_status ?? this._model?.arrived_at_hub_to_internal_status ?? this._aggregatorsInfoStore.getIsArrivedAtHubToInternalStatusInitialValue(this.aggregatorCode); }
    public get shouldCreateLabelOnExchangeOrder() { return this._changes.should_create_label_on_exchange_order ?? this._model?.should_create_label_on_exchange_order ?? true; }
    public get dangerousGoodsType() { return getNullableChangesOrModel(this._changes.dangerous_goods_type, this._model?.dangerous_goods_type) }

    private get shippingConnections(): IShippingConnection[]|undefined {
        return this._getShippingIntegrations()?.filter(conn =>
            !this._aggregatorsInfoStore.isDropOffAggregator(conn.aggregator_code)
        );
    }

    public get weightUnit() { return this._changes.weight_unit ?? this._model?.weight_unit; }
    public get aggregatorName() {
        return this.shippingConnections?.find(aggregator =>
                    aggregator.id === this.shippingConnectionId)?.internal_name;
    }
    public get aggregatorCode() {
        return this.shippingConnections?.find(aggregator =>
            aggregator.id === this.shippingConnectionId)?.aggregator_code;
    }

public get isCoverageConnection():boolean { return this.shippingConnections?.find( connection => connection.id === this.shippingConnectionId && this._aggregatorsInfoStore.isCoverage(connection.aggregator_code)) !== undefined; }

    public get carrierSelectionOptionsStore(): CarrierSelectionOptionsStore | undefined {
        return this._carrierSelectionOptionsStore;
    }

    public get isSupportCarrierSelectionOptions(): boolean {
        return this._aggregatorsInfoStore.getIsSupportCarrierSelectionOptions(this.aggregatorCode);
    }

    private _instantiateCarriersStore(): void {
        /** instantiating relevant carriers selection store according to connection */
        if (!this.shippingConnectionId) {
            this._carrierSelectionOptionsStore = undefined;
            this._defaultCarrierSelectorStore = undefined;
            return;
        }

        if(this.isSupportCarrierSelectionOptions) {
            this._defaultCarrierSelectorStore = undefined;
            this._carrierSelectionOptionsStore = new CarrierSelectionOptionsStore(
                this._getModel,
                () => this.shippingConnectionId as number,
                this._carrierOptionsCacheStore,
            );
            return;
        }

        this._carrierSelectionOptionsStore = undefined;
        this._defaultCarrierSelectorStore = new DefaultCarrierSelectorStore(
            this._getModel,
            this._getAddress,
            this._setUseCustomPackageIfNecessary,
            () => this.shippingConnectionId,
            () => this.useForwardLabels,
            () => this.isCoverageConnection,
            this._carrierOptionsCacheStore
        );
    }

    public mustHaveCustomDimensions(): boolean {
        return this._aggregatorsInfoStore.getUseCustomPackage(this.aggregatorCode) === CUSTOM_PACKAGE_SUPPORT_TYPE.USE_CUSTOM_PACKAGE_ALWAYS
            || (this._defaultCarrierSelectorStore?.hasAutoSelection() ?? false);
    }

    public hideCustomDimensions() {
        return this._aggregatorsInfoStore.getUseCustomPackage(this.aggregatorCode) === CUSTOM_PACKAGE_SUPPORT_TYPE.USE_CUSTOM_PACKAGE_NEVER;
    }

    public hasChanges(): boolean {
        return !!Object.keys(this._changes).length ||
         !!this._defaultCarrierSelectorStore?.hasChanges() ||
         !!this._carrierSelectionOptionsStore?.hasChanges();
    }

    public discardChanges(): void {
        this._changes = {};
        this._instantiateCarriersStore();
    }

    public setModel(model: IShippingLabelSettings | undefined ): void {
        this._model = model;
        this.discardChanges();
    }

    public getUpdatedModel(): IReturnLabelSettings | undefined {
        /** we construct this store locally just make sure to deleted carrier options when they are not relevant for specific aggregator */
        let carrierSelectionOptionsStore = this._carrierSelectionOptionsStore;
        if (!carrierSelectionOptionsStore) {
            carrierSelectionOptionsStore = new CarrierSelectionOptionsStore(
                this._getModel,
                () => this.shippingConnectionId as number,
                this._carrierOptionsCacheStore,
            );
            /** this will instruct the server to delete all carrier selection records (return_label_setting_carriers) when not relevant */
            carrierSelectionOptionsStore.resetSelections();
        }
        
        const updatedModel = {
            is_box_free: this._aggregatorsInfoStore.getBoxFreeInitialValue(this.aggregatorCode),
            ...this._model,
            ...this._defaultCarrierSelectorStore?.getUpdatedModel(),
            ...carrierSelectionOptionsStore.getUpdatedModel(),
            ...this._changes,
            _has_changes: this.hasChanges()
        } as IReturnLabelSettings;

        const isNewAndDeleted = !(this._model ?? {}).id && this.deleted;
        if (isNewAndDeleted) {
            return undefined;
        }

        return updatedModel;
    }

    public setDeleted(v: boolean) {
        this._changes._deleted = v;
    }

    public getTrackingType() {
        return this._aggregatorsInfoStore.getTrackingType(this.aggregatorCode);
    }

    public getTrackingNoteContent() {
        return this._aggregatorsInfoStore.getTrackingNoteContent(this.aggregatorCode);
    }

    public getTrackingHelpLink(): string|undefined {
        return this._aggregatorsInfoStore.getTrackingHelpLink(this.aggregatorCode) || undefined;
    }

    public showTrackingNote() {
        return this.getTrackingType() === TRACKING_TYPE.TRACKING_TYPE_NOT_SUPPORTED;
    }

    public getDefaultSignatureNote() {
        return this._aggregatorsInfoStore.getDefaultSignatureNote(this.aggregatorCode);
    }

    public showTrackingWebhook() {
        return this.getTrackingType() === TRACKING_TYPE.TRACKING_TYPE_MANUAL_REGISTRATION;
    }

    public getAggregtorLabel() {
        return this._aggregatorsInfoStore.getAggregtorLabel(this.aggregatorCode);
    }

    public getTrackingWebhookUrl() {
        return `${this._webhookUrl}/${this.aggregatorCode}`;
    }

    public hideForwardLabelField() {
        return !this._aggregatorsInfoStore.isSupportedReturnLabel(this.aggregatorCode)
            || !this._aggregatorsInfoStore.isSupportedForwardLabel(this.aggregatorCode);
    }

    public setUseForwardLabels(v: boolean) {
        this._changes.use_forward_labels = v;
    }

    private _setLabelType(type: LABEL_TYPE): boolean {
        if (this.labelType === type) {
            return false;
        }
        this._changes.label_type = type;
        return true;
    }

    public setLabelType(type: LABEL_TYPE) {
        if (!this._setLabelType(type)) {
            return;
        }
        this._onTriggerInstructionsChange(ShippingInstructionsChangeTrigger.LABEL_TYPE);
    }

    public getQrCodesAreNotGuaranteedNote(getTranslation: (key: string, scope: string) => string): string|undefined {
        const shouldShow = [LABEL_TYPE.LABEL_TYPE_QR, LABEL_TYPE.LABEL_TYPE_PRINTABLE_AND_QR].includes(this.labelType as LABEL_TYPE);
        if (shouldShow) {
            return getTranslation("QR_CODES_ARE_NOT_GUARANTEED_NOTE", this._scope);
        }
        return undefined;
    }

    public setWeight(weight: number|null) {
        this._changes.weight = isSet(weight) ? Number(weight) : null;
    }

    public setPackageWeight(weight: number|null) {
        this._changes.package_weight = isSet(weight) ? Number(weight) : null;
    }

    public validateWeight(): IInputValitationResponse {
        if (!isSet(this.weight) || this.weight! <= 0) {
            return {
                isValid: false,
                messages: ["Weight must be a positive number."],
            };
        }
        return {
            isValid: true,
            messages: [],
        };
    }

    public setWeightUnit(unit: WEIGHT_UNIT) {
        this._changes.weight_unit = unit;
    }

    public setUseCustomPackage(v: boolean) {
        this._changes.use_custom_package = v;
    }

    public setCpHeight(v: number|null) {
        this._changes.cp_height = v;
    }
    public validateCpHeight(): IInputValitationResponse {
        if (this.useCustomPackage && (!isSet(this.cpHeight) || this.cpHeight < 1)) {
            return { isValid: false, messages: ['Height must be a positive number.'] };
        }   
        return { isValid: true, messages: [] };
    }

    public setCpLength(v: number|null) {
        this._changes.cp_length = v;
    }
    public validateCpLength(): IInputValitationResponse {
        if (this.useCustomPackage && (!isSet(this.cpLength) || this.cpLength < 1)) {
            return { isValid: false, messages: ['Length must be a positive number.'] };
        }   
        return { isValid: true, messages: [] };
    }

    public setCpWidth(v: number|null) {
        this._changes.cp_width = v;
    }
    public validateCpWidth(): IInputValitationResponse {
        if (this.useCustomPackage && (!isSet(this.cpWidth) || this.cpWidth < 1)) {
            return { isValid: false, messages: ['Width must be a positive number.'] };
        }   
        return { isValid: true, messages: [] };
    }

    public setCpDimensionsUnit(v: CP_DIMENSION_UNIT) {
        this._changes.cp_dimension_unit = v;
    }

    private _setUseCustomPackageIfNecessary() {
        if (this.mustHaveCustomDimensions()) {
            this.setUseCustomPackage(true);
        }
    }

    public setRequireSignature(v: boolean) {
        this._changes.is_signature_confirmation_required = v;
    }

    public setBoxFree(v: boolean) {
        this._changes.is_box_free = v;
    }

    public setDefaultHsCode(v: string) {
        this._changes.default_hs_code = v;
    }
    public setDangerousGoodsType(v: DANGEROUS_GOODS_TYPE) {
        this._changes.dangerous_goods_type = v !== DANGEROUS_GOODS_TYPE.NOT_DANGEROUS ? v : null;
    }

    public setDefaultCustomerCompany(v: string) {
        this._changes.default_customer_company = v;
    }

    public setShouldCreateLabelOnExchangeOrder(v: boolean) {
        this._changes.should_create_label_on_exchange_order = v;
    }

    public setIgnoreAddressValidation(v: boolean) {
        this._changes.ignore_address_validation = v;
    }

    public populateAggregatorsList(): IOption[] {
        const aggregatorsList: IOption[] = [];
        this.shippingConnections?.forEach( integration =>
            aggregatorsList.push({
                value: integration.id,
                label: integration.internal_name
            })
        );
        return aggregatorsList;
    }

    public isDefaultWeightRequired(): boolean {
        return this._aggregatorsInfoStore.isDefaultWeightRequired(this.aggregatorCode);
    }

    private _shouldSendOrderNumber(): boolean {
        return this._aggregatorsInfoStore.getShouldSendOrderNumber(this.aggregatorCode);
    }

    public async setShippingConnectionId(id: number) {
        if (this.shippingConnectionId === id) {
            return;
        }
        this._changes.shipping_connection_id = id;
        this._changes.send_order_number = this._shouldSendOrderNumber();

        /** clear previos selections */
        this._changes.arrived_at_hub_to_internal_status = null;
        this._changes.is_box_free = undefined;
        this._changes.default_hs_code = undefined;
        this._changes.should_create_label_on_exchange_order = undefined;
        this._changes.dangerous_goods_type = undefined;

        /**  reset carriers and services**/
        this._instantiateCarriersStore();
        this._defaultCarrierSelectorStore?.resetSelections();
        this._carrierSelectionOptionsStore?.resetSelections();

        /** set use forward label according to aggregator's info */
        /** The default is Return labels - so if aggregator supprts return, set return */
        this.setUseForwardLabels(
            !this._aggregatorsInfoStore.isSupportedReturnLabel(this.aggregatorCode)
        );

        /** set custom package toggle button according to aggregator's info */
        if (this.mustHaveCustomDimensions()) {
            this.setUseCustomPackage(true);
        };

        if (this.hideCustomDimensions()) {
            this.setUseCustomPackage(false);
        };

        /** set label type according to aggregator's info */
        const labelType = this._aggregatorsInfoStore.getDefaultLabelType(this.aggregatorCode);
        this._setLabelType(labelType);

        /** set default value for carrier notifications */
        this._setDefaultCarrierNotifications();

        /** set default weight if aggregator info does not requires weight */
        if (!this.isDefaultWeightRequired()) {
            this.setWeight(1);
            this.setWeightUnit(WEIGHT_UNIT.GRAM);
        }

        /** set default arrived at hub status mapping **/
        if (this.showArrivedAtHubToInternalStatusDropdown()) {
            this.setArrivedAtHubToInternalStatus(this._aggregatorsInfoStore.getIsArrivedAtHubToInternalStatusInitialValue(this.aggregatorCode));
        }

        /** when address is not configurable for this aggregator we delete it */
        this._addressStore.setDeleted(
            !this._aggregatorsInfoStore.isAddressConfigurable(this.aggregatorCode)
        );

        this._onTriggerInstructionsChange(ShippingInstructionsChangeTrigger.SHIPPING_CONNECTION);

        /** for coverage connection- we set the carrier and service with hard-coded value, and hide those fields on UI.
         *  Also, we MUST set the carrier notifications to use email address. Default is merchant's email.
         */
        if (this.isCoverageConnection) {
            this._defaultCarrierSelectorStore?.setAllOverriddenByConnection();
            this._changes.carrier_notifications = CARRIER_NOTIFICATION.USE_MERCHANT_EMAIL_AS_TO_EMAIL;
        }
        else {
            await this._defaultCarrierSelectorStore?.setDefaultCarrierCode();
            await this._carrierSelectionOptionsStore?.setDefaultValues();
        }
    }

    private _getAddress(): IReturnMethodAddress|undefined {
        if (this._addressStore.isEmpty()) {
            return undefined;
        }
        return this._addressStore.getUpdatedModel();
    }


    public showCarriersField() {
        if (this.aggregatorCode) {
            return !this._aggregatorsInfoStore.isSingleCarrier(this.aggregatorCode);
        }
        return true;
    }

    public populateLabelTypeList(getTranslation: (key: string, scope: string) => string) {
        const labelTypes = this._aggregatorsInfoStore.getLabelTypeSupport(this.aggregatorCode) ?? {};
        const supportedLabelType = Object.keys(labelTypes).filter( type => !!labelTypes[type] );

        return supportedLabelType.map(type => {
            return {
                label: getTranslation(this.getLabelTypeName(type) ?? '', this._scope),
                value: type,
            }
        })
    }

    public setCarrierNotificationsList(getTranslation: (key: string, scope: string) => string): IOption[] {
        const carrierNotificationsList = [
            {
                label: getTranslation("USE_CUSTOMER_EMAIL", this._scope),
                value: CARRIER_NOTIFICATION.USE_CUSTOMER_EMAIL_AS_TO_EMAIL,
            },
            {
                label: getTranslation("USE_MERCHANT_EMAIL", this._scope),
                value: CARRIER_NOTIFICATION.USE_MERCHANT_EMAIL_AS_TO_EMAIL,
            },
        ]
        if (!this._aggregatorsInfoStore.isEmailAdressRequiredForCarrier(this.aggregatorCode)){
            carrierNotificationsList.unshift({
                label: getTranslation("USE_ONLY_FROM_EMAIL", this._scope),
                value: CARRIER_NOTIFICATION.DO_NOT_SEND_EMAIL_ADDRESS_TO_CARRIER,
            });
        }
        return carrierNotificationsList;
    }

    public setCarrierNotifications(notification: CARRIER_NOTIFICATION) {
        this._changes.carrier_notifications = notification;
    }

    private _setDefaultCarrierNotifications() {
        if (!this.carrierNotifications) {
            this._changes.carrier_notifications = CARRIER_NOTIFICATION.DO_NOT_SEND_EMAIL_ADDRESS_TO_CARRIER;
        }
    }

    private getLabelTypeName(type: string) {
        switch (type) {
            case LABEL_TYPE.LABEL_TYPE_PRINTABLE:
                return 'ONLY_A_PRINTABLE_LABEL';

            case LABEL_TYPE.LABEL_TYPE_QR:
                return 'ONLY_A_QR_CODE';

            case LABEL_TYPE.LABEL_TYPE_PRINTABLE_AND_QR:
                return 'QR_CODE_AND_OR_PRINTABLE_LABEL';
        }
    }

    public populateWeightUnitList() {
        return([
            {
                label:'Grams',
                value:WEIGHT_UNIT.GRAM
            },
            {
                label:'Kilograms',
                value:WEIGHT_UNIT.KILOGRAM
            },
            {
                label:'Ounces',
                value:WEIGHT_UNIT.OUNCE
            },
            {
                label:'Pounds',
                value:WEIGHT_UNIT.POUND
            }
        ])
    }

    async load() {
        if (this.isCoverageConnection) {
            return;
        }
        this._defaultCarrierSelectorStore?.load();
        this._carrierSelectionOptionsStore?.load();
	}

    public showRequireSugnatureToggle() {
        return this._aggregatorsInfoStore.getRequireSignature(this.aggregatorCode) ?? false;
    }

    public showBoxFreeToggle() {
        return this._aggregatorsInfoStore.isBoxFreeSupported(this.aggregatorCode);
    }

    public isBoxFreeDisabled() {
        return !this._aggregatorsInfoStore.isBoxFreeConfigurable(this.aggregatorCode);
    }

    public showDefaultHsCode() {
        return this._aggregatorsInfoStore.isDefaultHsCodeSupported(this.aggregatorCode);
    }

    public isDefaultHsCodeRequired() {
        return this._aggregatorsInfoStore.isDefaultHsCodeRequired(this.aggregatorCode);
    }

    public showDangerousGoodsDropdown() {
        return this._aggregatorsInfoStore.isDangerousGoodsSupported(this.aggregatorCode) ?? false;
    }

    public showIgnoreAddressValidationToggle() {
        return this._aggregatorsInfoStore.isIgnoreAddressValidationSupported(this.aggregatorCode) ?? false;
    }

    public getLabelTypeSupportNote() {
        return this._aggregatorsInfoStore.getLabelTypeSupportNote(this.aggregatorCode);
    }

    public showArrivedAtHubToInternalStatusDropdown(): boolean {
        return this._aggregatorsInfoStore.isArrivedAtHubToInternalStatusSupported(this.aggregatorCode);
    }

    public populateArrivedAtHubToInternalStatusList(getTranslation: (key: string, scope: string) => string): IOption[] {
        return this._aggregatorsInfoStore.getIsArrivedAtHubToInternalStatusOptionsList(this.aggregatorCode, getTranslation);
    }

    public setArrivedAtHubToInternalStatus(type: SHIPMENT_STATUS_ENUM): void {
        this._changes.arrived_at_hub_to_internal_status = type;
    }

    public getLabelSettingsNote(): string|null|undefined {
        return this._aggregatorsInfoStore.getLabelSettingsNote(this.aggregatorCode);
    }

    public isCreateLabelOnExchangeConfigurable(): boolean {
        return this._aggregatorsInfoStore.isCreateLabelOnExchangeConfigurable(this.aggregatorCode);
    }

    public populateDangerousGoodsTypeList(getTranslation: (key: string, scope: string) => string): IOption[] {
        return this._aggregatorsInfoStore.getDangerousGoodsTypeOptionsList(getTranslation);
    }
}
